import { Box, Typography, useTheme } from "@mui/material";
import React from "react";
import { EGSpeedDial } from "../../../../../../MainComponents/Button/SpeedDial/SpeedDial";
import { OutlineEdit, TrashCan } from "../../../../../../Icon";
import { useTranslation } from "react-i18next";
import Comments from "../../../../Information/Comments";
import { useReportContext } from "../../../../../ReportContext/useReportContext";
import {
  getActionLabel,
  getActionPath,
  normalizeResolutionMeasure,
} from "../../../../../../../helpers/correctiveActions";
import { ActionOrigin, DiscardedProposals } from "./ResolutionOrigin";

export const Measures = ({
  measures,
  hdlOpenDialog,
  openModalConfirm,
  hdlDeleteMeasure,
  closeModalConfirm,
}) => {
  const theme = useTheme();
  const [t] = useTranslation("report");
  const { measureOptions } = useReportContext();

  // Las medidas nuevas ya guardan su etiqueta legible junto a la ruta. Las
  // guardadas antes de la homologacion solo tienen el value hardcodeado, asi
  // que se resuelve contra measureOptions y, como ultimo recurso, se muestra el
  // value crudo para no dejarla en blanco.
  const resolveLabel = (action) => {
    const label = getActionLabel(action);
    const path = getActionPath(action);
    if (label && label !== path) return label;
    return measureOptions.find((option) => option.value === path)?.label || path;
  };

  // Se listan una por una (y no en una sola linea) para poder decir de que
  // investigacion viene cada medida: en un mismo registro conviven las que
  // propuso el investigador y las que agrego el resolutor.
  const getRows = (measure) => {
    const { actions, another } = normalizeResolutionMeasure(measure);
    return [
      ...actions.map((item) => ({ label: resolveLabel(item), action: item })),
      ...another.map((item) => ({ label: item.text, action: item })),
    ];
  };

  return (
    <Box
      px={1.5}
      maxHeight={"40vh"}
      sx={{ overflowY: "auto" }}
      className="rounded"
    >
      {measures.map((measure, i) => (
        <Box
          key={measure.id}
          className={`${i < measures.length - 1 ? "border-bottom" : ""}`}
          sx={{
            py: 1.3,
            minHeight: "50px",
            position: "relative",
            my: "auto",
          }}
        >
          <EGSpeedDial
            sx={{ position: "absolute", right: -4, top: 1.5, scale: 0.8 }}
            tooltipPlacement="top"
            actions={[
              {
                icon: <OutlineEdit color={theme.palette.blue.main} />,
                name: t("Resolution.edit"),
                direction: "top",
                onClick: () => hdlOpenDialog("measures", measure),
              },
              {
                icon: <TrashCan color={theme.palette.error.main} />,
                name: t("Resolution.delete"),
                direction: "top",
                onClick: () =>
                  openModalConfirm({
                    variant: "error",
                    title: t("Resolution.attention"),
                    titleColor: theme.palette.pink.main,
                    description1: t(
                      "Care.deleting_this_measure_will_permanently_erase_all_content"
                    ),
                    question: t("Care.do_you_want_continue"),
                    leftButtonText: t("Resolution.delete"),
                    leftButtonColor: "error",
                    leftButtonVariant: "outlined",
                    onClickLeft: () => hdlDeleteMeasure(measure),
                    rightButtonText: t("Care.cancel"),
                    rightButtonColor: "secondary",
                    onClickRight: () => closeModalConfirm(),
                    ethiquin: "megaphone",
                  }),
              },
            ]}
          />
          {/* El encabezado va en su propia linea: una medida puede tener
              varias acciones y cada una su origen. */}
          <Typography
            color="secondary"
            fontWeight={600}
            variant="body1"
            component="div"
          >
            {`${t("Resolution.measure")} #${i + 1}:`}
          </Typography>

          <Box pl={1}>
            {getRows(measure).map((row, idx) => (
              <Box key={`measure-${measure.id}-${idx}`} mb={0.4}>
                <Typography
                  color="primary"
                  variant="body1"
                  component="div"
                  fontWeight={700}
                  lineHeight={1.4}
                >
                  {row.label}
                </Typography>
                <ActionOrigin action={row.action} />
              </Box>
            ))}
          </Box>

          {measure.comment && (
            <Comments
              variant="body1"
              text={measure.comment}
              background="white"
              padding={0.33}
              maxLength={250}
            />
          )}

          <DiscardedProposals discarded={measure.discarded} />
        </Box>
      ))}
    </Box>
  );
};
